import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Ticket, 
  Plus, 
  Send, 
  CheckCircle,
  Clock,
  Trash2,
  Loader2,
  ArrowUpCircle,
  ArrowDownCircle,
  AlertTriangle,
  RefreshCw,
  FileText,
  BadgeCheck,
  CircleDashed
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const VouchersPage = () => {
  const { stores, selectedStore, setSelectedStore } = useStore();
  
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [transactionFilter, setTransactionFilter] = useState('all');
  const [creditNotes, setCreditNotes] = useState([]);
  const [creditNotesLoading, setCreditNotesLoading] = useState(false);
  const [creditNoteFilter, setCreditNoteFilter] = useState('all'); // all, pending, reconciled
  
  // Dialog states
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [submitReturnDialogOpen, setSubmitReturnDialogOpen] = useState(false);
  const [confirmReturnDialogOpen, setConfirmReturnDialogOpen] = useState(false);
  const [selectedPendingReturn, setSelectedPendingReturn] = useState(null);
  
  // Form states
  const [adjustmentForm, setAdjustmentForm] = useState({
    amount: '',
    adjustment_type: 'adjustment',  // 'adjustment' or 'rejected'
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [submitReturnForm, setSubmitReturnForm] = useState({
    supplier_id: '',
    supplier_name: '',
    voucher_count: '',
    total_value: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [confirmReturnForm, setConfirmReturnForm] = useState({
    accepted_amount: '',
    rejected_amount: '0',
    rejected_action: 'write_off',
    notes: ''
  });
  
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedStore?.id) {
      setSelectedStoreId(selectedStore.id);
    }
  }, [selectedStore]);

  useEffect(() => {
    if (selectedStoreId) {
      fetchVoucherData();
      fetchSuppliers();
      fetchCreditNotes();
    }
  }, [selectedStoreId]);

  const fetchVoucherData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/vouchers`, {
        params: { store_id: selectedStoreId }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch voucher data:', error);
      toast.error('Failed to load voucher data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/api/suppliers`, {
        params: { store_id: selectedStoreId }
      });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers');
    }
  };

  const fetchCreditNotes = async () => {
    if (!selectedStoreId) return;
    setCreditNotesLoading(true);
    try {
      // Fetch all credit notes (both pending and reconciled)
      const [pendingRes, reconciledRes] = await Promise.all([
        axios.get(`${API}/api/reconciliation/voucher-credit-notes?store_id=${selectedStoreId}&reconciled=false`),
        axios.get(`${API}/api/reconciliation/voucher-credit-notes?store_id=${selectedStoreId}&reconciled=true`)
      ]);
      const allCreditNotes = [...(pendingRes.data || []), ...(reconciledRes.data || [])];
      // Sort by date (newest first)
      allCreditNotes.sort((a, b) => new Date(b.date) - new Date(a.date));
      setCreditNotes(allCreditNotes);
    } catch (error) {
      console.error('Failed to fetch credit notes:', error);
    } finally {
      setCreditNotesLoading(false);
    }
  };

  const handleStoreChange = (storeId) => {
    setSelectedStoreId(storeId);
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setSelectedStore(store);
    }
  };

  const handleAdjustment = async () => {
    if (!adjustmentForm.amount) {
      toast.error('Please enter an amount');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/api/vouchers/adjustment`, {
        store_id: selectedStoreId,
        amount: parseFloat(adjustmentForm.amount),
        adjustment_type: adjustmentForm.adjustment_type,
        date: adjustmentForm.date,
        notes: adjustmentForm.notes
      });
      
      toast.success(adjustmentForm.adjustment_type === 'rejected' ? 'Voucher rejection recorded' : 'Adjustment saved');
      setAdjustmentDialogOpen(false);
      setAdjustmentForm({ amount: '', adjustment_type: 'adjustment', date: new Date().toISOString().split('T')[0], notes: '' });
      fetchVoucherData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save adjustment');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReturn = async () => {
    if (!submitReturnForm.supplier_id || !submitReturnForm.total_value) {
      toast.error('Please select supplier and enter total value');
      return;
    }
    
    setSaving(true);
    try {
      const supplier = suppliers.find(s => s.id === submitReturnForm.supplier_id);
      
      await axios.post(`${API}/api/vouchers/submit-return`, {
        store_id: selectedStoreId,
        supplier_id: submitReturnForm.supplier_id,
        supplier_name: supplier?.name || 'Unknown',
        voucher_count: parseInt(submitReturnForm.voucher_count) || 0,
        total_value: parseFloat(submitReturnForm.total_value),
        date: submitReturnForm.date,
        notes: submitReturnForm.notes
      });
      
      toast.success('Voucher return submitted. Waiting for supplier confirmation.');
      setSubmitReturnDialogOpen(false);
      setSubmitReturnForm({
        supplier_id: '',
        supplier_name: '',
        voucher_count: '',
        total_value: '',
        date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      fetchVoucherData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit return');
    } finally {
      setSaving(false);
    }
  };

  const openConfirmReturnDialog = (pendingReturn) => {
    setSelectedPendingReturn(pendingReturn);
    const originalAmount = Math.abs(pendingReturn.amount);
    setConfirmReturnForm({
      accepted_amount: originalAmount.toString(),
      rejected_amount: '0',
      rejected_action: 'write_off',
      notes: ''
    });
    setConfirmReturnDialogOpen(true);
  };

  const handleConfirmReturn = async () => {
    if (!confirmReturnForm.accepted_amount) {
      toast.error('Please enter accepted amount');
      return;
    }
    
    const originalAmount = Math.abs(selectedPendingReturn.amount);
    const accepted = parseFloat(confirmReturnForm.accepted_amount);
    const rejected = parseFloat(confirmReturnForm.rejected_amount) || 0;
    
    if (Math.abs((accepted + rejected) - originalAmount) > 0.01) {
      toast.error(`Accepted + Rejected must equal £${originalAmount.toFixed(2)}`);
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/api/vouchers/confirm-return/${selectedPendingReturn.id}`, {
        accepted_amount: accepted,
        rejected_amount: rejected,
        rejected_action: confirmReturnForm.rejected_action,
        notes: confirmReturnForm.notes
      });
      
      toast.success(`Voucher return confirmed. Credit note created: £${accepted.toFixed(2)}`);
      setConfirmReturnDialogOpen(false);
      setSelectedPendingReturn(null);
      fetchVoucherData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm return');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPending = async (transactionId) => {
    if (!window.confirm('Are you sure you want to cancel this pending return?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/api/vouchers/pending/${transactionId}`);
      toast.success('Pending return cancelled');
      fetchVoucherData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount || 0);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'received':
        return <ArrowDownCircle className="h-4 w-4 text-green-500" />;
      case 'return_submitted':
        return <Send className="h-4 w-4 text-amber-500" />;
      case 'adjustment':
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case 'rejected_returned':
        return <ArrowDownCircle className="h-4 w-4 text-amber-500" />;
      case 'write_off':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Ticket className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'received': return 'Received from Sales';
      case 'return_submitted': return 'Returned to Supplier';
      case 'adjustment': return 'Manual Adjustment';
      case 'rejected_returned': return 'Rejected - Returned to Balance';
      case 'rejected_adjustment': return 'Rejected (Manual)';
      case 'write_off': return 'Write Off (Supplier Rejection)';
      default: return type;
    }
  };

  // Filter transactions based on selected filter
  const getFilteredTransactions = () => {
    if (!data?.transactions) return [];
    if (transactionFilter === 'all') return data.transactions;
    
    return data.transactions.filter(txn => {
      switch (transactionFilter) {
        case 'received':
          return txn.transaction_type === 'received';
        case 'returns':
          return txn.transaction_type === 'return_submitted';
        case 'adjustments':
          return txn.transaction_type === 'adjustment';
        case 'rejected':
          return ['rejected_adjustment', 'write_off', 'rejected_returned'].includes(txn.transaction_type);
        default:
          return true;
      }
    });
  };

  const filteredTransactions = getFilteredTransactions();

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Ticket className="h-6 w-6" />
            Voucher Management
          </h1>
          <p className="text-muted-foreground">Track voucher balances and supplier returns</p>
        </div>
        
        <div className="flex gap-2">
          <Select value={selectedStoreId} onValueChange={handleStoreChange}>
            <SelectTrigger className="w-[200px]" data-testid="store-select">
              <SelectValue placeholder="Select Store" />
            </SelectTrigger>
            <SelectContent>
              {stores.map(store => (
                <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : !selectedStoreId ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Please select a store to view voucher data</p>
          </CardContent>
        </Card>
      ) : data?.voucher_enabled === false ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-amber-500 opacity-70" />
            <p className="text-lg font-medium mb-2">Voucher Payment Not Enabled</p>
            <p className="text-sm max-w-md mx-auto">
              This store does not have "Voucher" enabled as a payment method. 
              To use voucher management, go to <strong>Store Management → Edit Store → Payment Methods</strong> and add "Voucher" as a payment type.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-green-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <Ticket className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Available Balance</p>
                    <p className="text-2xl font-bold text-green-500">
                      {formatCurrency(data?.balance || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-amber-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-500/10 rounded-lg">
                    <Clock className="h-6 w-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Returns</p>
                    <p className="text-2xl font-bold text-amber-500">
                      {formatCurrency(data?.pending_amount || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-red-500/20">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-500/10 rounded-lg">
                    <AlertTriangle className="h-6 w-6 text-red-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Rejected Vouchers</p>
                    <p className="text-2xl font-bold text-red-500">
                      {formatCurrency(data?.rejected_balance || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setAdjustmentDialogOpen(true)} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Adjustment
                  </Button>
                  <Button onClick={() => setSubmitReturnDialogOpen(true)} disabled={!data?.balance || data.balance <= 0}>
                    <Send className="h-4 w-4 mr-2" />
                    Submit to Supplier
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Returns */}
          {data?.pending_returns && data.pending_returns.length > 0 && (
            <Card className="border-amber-500/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-500">
                  <Clock className="h-5 w-5" />
                  Pending Supplier Confirmations ({data.pending_returns.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {data.pending_returns.map(pending => (
                    <div 
                      key={pending.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-amber-500/30 bg-amber-500/5"
                    >
                      <div>
                        <p className="font-semibold">{pending.supplier_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {pending.voucher_count} vouchers • Submitted {format(new Date(pending.created_at), 'dd MMM yyyy')}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-bold text-amber-500">
                          {formatCurrency(Math.abs(pending.amount))}
                        </span>
                        <Button 
                          size="sm" 
                          onClick={() => openConfirmReturnDialog(pending)}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Confirm
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => handleCancelPending(pending.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Transaction History */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Transaction History
              </CardTitle>
              <Select value={transactionFilter} onValueChange={setTransactionFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="received">Received (Sales)</SelectItem>
                  <SelectItem value="returns">Returns to Supplier</SelectItem>
                  <SelectItem value="adjustments">Adjustments</SelectItem>
                  <SelectItem value="rejected">Rejected/Write-offs</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              {filteredTransactions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{transactionFilter === 'all' ? 'No voucher transactions yet' : 'No transactions match the selected filter'}</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {filteredTransactions.map(txn => (
                    <div 
                      key={txn.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-full bg-muted">
                          {getTransactionIcon(txn.transaction_type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{getTransactionLabel(txn.transaction_type)}</p>
                            {txn.status === 'pending' && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-500">
                                Pending
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(txn.date || txn.created_at), 'dd MMM yyyy')}
                            {txn.supplier_name && ` • ${txn.supplier_name}`}
                            {txn.notes && ` • ${txn.notes}`}
                          </p>
                        </div>
                      </div>
                      <span className={`font-bold ${txn.amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {txn.amount >= 0 ? '+' : ''}{formatCurrency(txn.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Voucher Credit Notes Card */}
          <Card className="mt-6">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-600" />
                  Voucher Credit Notes
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={creditNoteFilter} onValueChange={setCreditNoteFilter}>
                    <SelectTrigger className="w-[140px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reconciled">Reconciled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={fetchCreditNotes}
                    disabled={creditNotesLoading}
                  >
                    <RefreshCw className={`h-4 w-4 ${creditNotesLoading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Credit notes from accepted voucher returns. Use in Payment Reconciliation to offset supplier invoices.
              </p>
            </CardHeader>
            <CardContent>
              {creditNotesLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : creditNotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No credit notes yet</p>
                  <p className="text-xs mt-1">Credit notes are created when suppliers accept returned vouchers</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {creditNotes
                    .filter(cn => {
                      if (creditNoteFilter === 'pending') return !cn.is_reconciled;
                      if (creditNoteFilter === 'reconciled') return cn.is_reconciled;
                      return true;
                    })
                    .map((cn) => (
                    <div 
                      key={cn.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        cn.is_reconciled 
                          ? 'bg-green-500/5 border-green-500/20' 
                          : 'bg-amber-500/5 border-amber-500/20'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          cn.is_reconciled ? 'bg-green-500/20' : 'bg-amber-500/20'
                        }`}>
                          {cn.is_reconciled ? (
                            <BadgeCheck className="h-4 w-4 text-green-600" />
                          ) : (
                            <CircleDashed className="h-4 w-4 text-amber-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{cn.credit_note_number}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              cn.is_reconciled 
                                ? 'bg-green-500/20 text-green-600' 
                                : 'bg-amber-500/20 text-amber-600'
                            }`}>
                              {cn.is_reconciled ? 'Reconciled' : 'Pending'}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {cn.supplier_name} • {format(new Date(cn.date), 'dd MMM yyyy')}
                            {cn.voucher_count && ` • ${cn.voucher_count} vouchers`}
                          </p>
                          {cn.is_reconciled && cn.reconciled_date && (
                            <p className="text-xs text-green-600">
                              Reconciled on {format(new Date(cn.reconciled_date), 'dd MMM yyyy')}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="font-bold text-green-600">
                        {formatCurrency(cn.amount)}
                      </span>
                    </div>
                  ))}
                  
                  {/* Summary */}
                  <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <div className="text-sm text-muted-foreground">
                      <span className="font-medium">{creditNotes.filter(cn => !cn.is_reconciled).length}</span> pending, 
                      <span className="font-medium ml-1">{creditNotes.filter(cn => cn.is_reconciled).length}</span> reconciled
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Total Pending</p>
                      <p className="font-bold text-green-600">
                        {formatCurrency(creditNotes.filter(cn => !cn.is_reconciled).reduce((sum, cn) => sum + cn.amount, 0))}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Adjustment Dialog */}
      <Dialog open={adjustmentDialogOpen} onOpenChange={setAdjustmentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5" />
              Manual Adjustment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Use this for initial balance setup or corrections. Enter positive value to add, negative to subtract.
            </p>
            
            <div>
              <Label>Adjustment Type</Label>
              <RadioGroup 
                value={adjustmentForm.adjustment_type} 
                onValueChange={(value) => setAdjustmentForm({ ...adjustmentForm, adjustment_type: value })}
                className="flex gap-4 mt-2"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="adjustment" id="adj-normal" />
                  <Label htmlFor="adj-normal" className="cursor-pointer">Add/Remove Balance</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="rejected" id="adj-rejected" />
                  <Label htmlFor="adj-rejected" className="cursor-pointer text-red-500">Mark as Rejected</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div>
              <Label>Amount (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={adjustmentForm.amount}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, amount: e.target.value })}
                placeholder={adjustmentForm.adjustment_type === 'rejected' ? "e.g., 50.00" : "e.g., 100.00 or -50.00"}
              />
              {adjustmentForm.adjustment_type === 'rejected' && (
                <p className="text-xs text-muted-foreground mt-1">This amount will be deducted from available balance and tracked as rejected</p>
              )}
            </div>
            
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={adjustmentForm.date}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, date: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={adjustmentForm.notes}
                onChange={(e) => setAdjustmentForm({ ...adjustmentForm, notes: e.target.value })}
                placeholder={adjustmentForm.adjustment_type === 'rejected' ? "Reason for rejection..." : "Reason for adjustment..."}
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustmentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdjustment} disabled={saving} variant={adjustmentForm.adjustment_type === 'rejected' ? 'destructive' : 'default'}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {adjustmentForm.adjustment_type === 'rejected' ? 'Record Rejection' : 'Save Adjustment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Return Dialog */}
      <Dialog open={submitReturnDialogOpen} onOpenChange={setSubmitReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Submit Vouchers to Supplier
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm">Available Balance: <strong className="text-green-500">{formatCurrency(data?.balance || 0)}</strong></p>
            </div>
            
            <div>
              <Label>Supplier *</Label>
              <Select 
                value={submitReturnForm.supplier_id} 
                onValueChange={(v) => setSubmitReturnForm({ ...submitReturnForm, supplier_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Supplier" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Number of Vouchers</Label>
                <Input
                  type="number"
                  value={submitReturnForm.voucher_count}
                  onChange={(e) => setSubmitReturnForm({ ...submitReturnForm, voucher_count: e.target.value })}
                  placeholder="e.g., 50"
                />
              </div>
              <div>
                <Label>Total Value (£) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={submitReturnForm.total_value}
                  onChange={(e) => setSubmitReturnForm({ ...submitReturnForm, total_value: e.target.value })}
                  placeholder="e.g., 125.00"
                />
              </div>
            </div>
            
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={submitReturnForm.date}
                onChange={(e) => setSubmitReturnForm({ ...submitReturnForm, date: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={submitReturnForm.notes}
                onChange={(e) => setSubmitReturnForm({ ...submitReturnForm, notes: e.target.value })}
                placeholder="Reference number, etc..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitReturnDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitReturn} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Return Dialog */}
      <Dialog open={confirmReturnDialogOpen} onOpenChange={setConfirmReturnDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Confirm Supplier Response
            </DialogTitle>
          </DialogHeader>
          
          {selectedPendingReturn && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="font-semibold">{selectedPendingReturn.supplier_name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedPendingReturn.voucher_count} vouchers submitted • Total: {formatCurrency(Math.abs(selectedPendingReturn.amount))}
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Accepted Amount (£) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={confirmReturnForm.accepted_amount}
                    onChange={(e) => {
                      const accepted = parseFloat(e.target.value) || 0;
                      const original = Math.abs(selectedPendingReturn.amount);
                      const rejected = Math.max(0, original - accepted);
                      setConfirmReturnForm({ 
                        ...confirmReturnForm, 
                        accepted_amount: e.target.value,
                        rejected_amount: rejected.toFixed(2)
                      });
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Amount supplier will credit
                  </p>
                </div>
                <div>
                  <Label>Rejected Amount (£)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={confirmReturnForm.rejected_amount}
                    onChange={(e) => {
                      const rejected = parseFloat(e.target.value) || 0;
                      const original = Math.abs(selectedPendingReturn.amount);
                      const accepted = Math.max(0, original - rejected);
                      setConfirmReturnForm({ 
                        ...confirmReturnForm, 
                        rejected_amount: e.target.value,
                        accepted_amount: accepted.toFixed(2)
                      });
                    }}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Amount rejected by supplier
                  </p>
                </div>
              </div>
              
              {parseFloat(confirmReturnForm.rejected_amount) > 0 && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <Label className="text-amber-600">How to handle rejected vouchers?</Label>
                  <RadioGroup 
                    value={confirmReturnForm.rejected_action}
                    onValueChange={(v) => setConfirmReturnForm({ ...confirmReturnForm, rejected_action: v })}
                    className="mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="write_off" id="write_off" />
                      <Label htmlFor="write_off" className="cursor-pointer">
                        Write Off - Mark as loss
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="return_to_balance" id="return_to_balance" />
                      <Label htmlFor="return_to_balance" className="cursor-pointer">
                        Return to Balance - Add back to resend later
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
              )}
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={confirmReturnForm.notes}
                  onChange={(e) => setConfirmReturnForm({ ...confirmReturnForm, notes: e.target.value })}
                  placeholder="Credit note reference, rejection reason, etc..."
                  rows={2}
                />
              </div>
              
              {parseFloat(confirmReturnForm.accepted_amount) > 0 && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <p className="text-sm text-green-600">
                    A <strong>credit note</strong> of {formatCurrency(parseFloat(confirmReturnForm.accepted_amount))} will be created for {selectedPendingReturn.supplier_name}.
                    This can be used to offset supplier invoices during reconciliation.
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReturnDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmReturn} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Return
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VouchersPage;
